// ListImageThumb — the 40x40 image-manager row thumbnail used by the shared
// ImageGrid <List> (which also powers the Carousel block's "Images" pane).
//
// QA-113: the row previously rendered a bare `<img src={item.src} />` with NO
// onError handler. When a media URL is broken / expired / encrypted-and-
// undecodable (the same class the Media Library hit), the browser paints a blank
// white box in the row — "rows 1 & 5 blank". This swaps that native broken glyph
// for a small, framed broken-image placeholder that keeps the row's shape stable,
// exactly mirroring the ImageBlock -> MediaFallback pattern (shouldShowMediaFallback:
// an empty/whitespace src is "no media yet", NOT an error).

import React, { useEffect, useState } from "react";
import { styled } from "styled-components";
import { shouldShowMediaFallback } from "../../Shared/MediaFallback/shouldShowFallback";

const Image = styled.img`
  width: 40px;
  height: 40px;
  border: 1px solid #667085;
  border-radius: 4px;
  object-fit: cover;
`;

// Framed broken-image placeholder, same 40x40 footprint + border so the row
// never reflows when a thumbnail fails. Inline line-art (never emoji); no network
// asset that could itself 404.
const Fallback = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid #667085;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f4f7;
  color: #98a2b3;
  box-sizing: border-box;
  svg {
    width: 22px;
    height: 22px;
  }
`;

const BrokenGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M3 15.5l4-4 3.5 3.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 14l2.5-2.5L21 16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M4 4.5l15.5 15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ListImageThumb = ({ src, alt = "" }) => {
  const [hasError, setHasError] = useState(false);

  // A changed src is a fresh attempt — clear any prior error so a replaced image
  // is not stuck on the fallback.
  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError) {
    return (
      <Fallback role="img" aria-label={alt ? `Image unavailable: ${alt}` : "Image unavailable"}>
        <BrokenGlyph />
      </Fallback>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      onError={() => {
        // Empty/whitespace src is "no media selected yet", not a failure.
        if (shouldShowMediaFallback(src)) setHasError(true);
      }}
    />
  );
};

export default ListImageThumb;
